// @flow

import type { PageProps } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import * as React from 'react'
import { getTrackAppUrl } from 'urls'

import type { AuthErrorLocationState } from '@toggl/auth'
import { Hero, useHasMounted } from '@toggl/ui'
import LoginForm from 'components/LoginForm'

const isTogglUrl = (str) => {
  if (str == null) {
    return false
  }

  if (str.charAt(0) === '/' && str.charAt(1) !== '/') {
    return true
  }

  return /^(https?:\/\/)?(www\.)?([^.]+\.)?toggl\.com/.exec(str) != null
}

const LoginPage = ({
  location,
}: PageProps<void, void, AuthErrorLocationState>) => {
  const expired = location.search.includes('expired')
  const returnTo = new URLSearchParams(location.search).get('returnTo')
  const hasMounted = useHasMounted()

  const HeroComponent = React.useMemo(
    () => ({ children }) => (
      <Hero.Wrapper
        bgImage={
          <StaticImage
            formats={['auto', 'webp', 'avif']}
            src='../../assets/hero-laptops.jpg'
            loading='eager'
            alt='Hero background image'
            layout='fullWidth'
            placeholder='none'
            objectFit='cover'
            backgroundColor='black'
          />
        }
      >
        {children}

        <Hero.LoginWrapper>
          <Hero.Title centered>
            {expired ? 'Please log in again' : 'Log in to your account'}
          </Hero.Title>
          <Hero.LoginSmallTitle centered>
            {expired
              ? 'You were logged out from your Toggl account'
              : "Let's get tracking!"}
          </Hero.LoginSmallTitle>
        </Hero.LoginWrapper>
      </Hero.Wrapper>
    ),
    [expired]
  )
  const onSuccess = React.useCallback(() => {
    const appUrl = getTrackAppUrl()
    if (returnTo && isTogglUrl(returnTo)) {
      window.location.assign(new URL(returnTo, appUrl))
    } else {
      window.location.assign(appUrl)
    }
  }, [returnTo])

  if (!hasMounted) {
    return null
  }

  return (
    <LoginForm
      title={'Log in to your account | Toggl Track'}
      description={
        'Log in to your Toggl Track account and dive into effortless time tracking and powerful reporting'
      }
      initialError={location.state?.error}
      onSuccess={onSuccess}
      Hero={HeroComponent}
      ssoClient='webapp'
    />
  )
}

export default LoginPage
